(() => {
  const vendorKey = value => value.normalize('NFC').trim().toLocaleLowerCase('pt-PT');

  async function fetchSection(url) {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  function showCount(item, count) {
    if (!Number.isInteger(count) || count < 0) return false;
    item.querySelector('[data-brand-count]').textContent = `(${count})`;
    return true;
  }

  async function hydrateCounts(root, items) {
    const totals = new Map();

    try {
      const document = await fetchSection(root.dataset.countsUrl);
      for (const facet of document.querySelectorAll('[data-brand-facet]')) {
        const key = vendorKey(facet.dataset.brandFacet);
        const count = Number(facet.dataset.brandCountValue);
        if (Number.isInteger(count) && count >= 0) {
          totals.set(key, (totals.get(key) || 0) + count);
        }
      }
    } catch (_) {
      // Individual vendor pages provide the fallback when facets are unavailable.
    }

    const missing = items.filter(item => !showCount(item, totals.get(vendorKey(item.dataset.vendor))));
    let next = 0;

    async function loadMissing() {
      while (next < missing.length) {
        const item = missing[next++];
        const url = new URL(item.querySelector('a').href);
        url.searchParams.set('section_id', 'brand-vendor-count');

        try {
          const document = await fetchSection(url);
          const value = document.querySelector('[data-brand-vendor-count]');
          if (value) showCount(item, Number(value.dataset.brandVendorCount));
        } catch (_) {
          // Never present an unverified number.
        }
      }
    }

    await Promise.all(Array.from({ length: Math.min(4, missing.length) }, loadMissing));
  }

  function initBrandDirectory(root) {
    if (root.dataset.searchReady) return;
    root.dataset.searchReady = 'true';

    const search = root.querySelector('[data-brand-search]');
    const searchForm = root.querySelector('[data-brand-search-form]');
    const clearButton = root.querySelector('[data-brand-search-clear]');
    const letterButtons = [...root.querySelectorAll('[data-brand-letter]')];
    const headings = [...root.querySelectorAll('[data-brand-heading]')];
    const items = [...root.querySelectorAll('[data-brand-item]')];
    if (!items.length) return;
    const status = root.querySelector('[data-brand-status]');
    const empty = root.querySelector('[data-brand-empty]');
    const normalize = value => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-PT');
    const formatStatus = (template, count, letter) => template.replace('%count%', count).replace('%letter%', letter);
    let selectedLetter = 'all';
    let appliedQuery = '';

    function renderResults() {
      let matches = 0;

      for (const item of items) {
        const visible = appliedQuery
          ? normalize(item.dataset.vendor).includes(appliedQuery)
          : selectedLetter === 'all' || item.dataset.letter === selectedLetter;
        item.hidden = !visible;
        if (visible) matches++;
      }

      for (const heading of headings) {
        let item = heading.nextElementSibling;
        let hasVisibleItem = false;
        while (item && !item.matches('[data-brand-heading]')) {
          if (!item.hidden) hasVisibleItem = true;
          item = item.nextElementSibling;
        }
        heading.hidden = !hasVisibleItem;
      }

      for (const button of letterButtons) {
        button.setAttribute('aria-pressed', String(!appliedQuery && button.dataset.brandLetter === selectedLetter));
      }

      if (empty) empty.hidden = matches > 0;
      if (status) status.textContent = appliedQuery
        ? formatStatus(matches === 1 ? root.dataset.statusFoundOne : root.dataset.statusFoundMany, matches, '')
        : selectedLetter === 'all' ? '' : formatStatus(matches === 1 ? root.dataset.statusLetterOne : root.dataset.statusLetterMany, matches, selectedLetter);
    }

    function applySearch() {
      appliedQuery = normalize(search.value.trim());
      selectedLetter = 'all';
      renderResults();
    }

    search.addEventListener('input', event => {
      clearButton.hidden = search.value.length === 0;
      if (event.isComposing) return;
      const query = normalize(search.value.trim());
      if (query.length >= 3 || query.length === 0) {
        appliedQuery = query;
        selectedLetter = 'all';
        renderResults();
      } else if (appliedQuery) {
        appliedQuery = '';
        renderResults();
      }
    });

    searchForm.addEventListener('submit', event => {
      event.preventDefault();
      applySearch();
    });

    clearButton.addEventListener('click', () => {
      search.value = '';
      clearButton.hidden = true;
      appliedQuery = '';
      selectedLetter = 'all';
      renderResults();
      search.focus();
    });

    letterButtons.forEach(button => {
      button.addEventListener('click', () => {
        selectedLetter = button.dataset.brandLetter;
        appliedQuery = '';
        search.value = '';
        clearButton.hidden = true;
        renderResults();
      });
    });

    hydrateCounts(root, items);
  }

  if (!customElements.get('brand-directory')) {
    customElements.define('brand-directory', class extends HTMLElement {
      connectedCallback() {
        initBrandDirectory(this);
      }
    });
  }
})();
